// IE Hub - Share Success Page
// Displays success message after sharing budget with companies

import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  Backdrop,
  useMediaQuery,
  Slide,
  useTheme,
} from "@/ui";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import BottomTabBar from "@/components/BottomTabBar";
import {
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Share as ShareIcon,
  Search as SearchIcon,
  ArrowForward as ArrowForwardIcon,
  FiberManualRecord as DotIcon,
} from "@mui/icons-material";
import AddCompanyDialog from "@/components/AddCompanyDialog";
import { LenderService } from "@/shared/api/services";
import { LenderParentRecord } from "@/shared/api/services/lenderService";
import {
  ApiResponse,
  CustomerDeclarationEntry,
  DigitalLender,
  ServiceResponse,
} from "@/shared/api/types";
import {
  customerEndpoints,
  declarationEndpoints,
} from "@/shared/api/endpoints";
import femaCapture from "@/shared/fema/capture";
import { httpGet } from "@/shared/api/http";
import { trackMetaEvent } from "@/shared/tracking/metaTracking";
import { getCustomerId } from "@/shared/lib/utils/getCustomerId";
import { Company } from "@/components/AddCompanyForm";
import { SharePageService } from "@/shared/api/services/sharePageService";

const Transition = React.forwardRef<
  unknown,
  { in?: boolean; children: React.ReactElement }
>(function Transition(props, ref) {
  return <Slide direction="up" ref={ref as React.Ref<unknown>} {...props} />;
});

const ShareSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [benefitsAmount, setBenefitsAmount] = useState("");
  const [balanceAmount, setBalanceAmount] = useState<number>(0);
  const [sharedCompanies, setSharedCompanies] = useState<string[]>([]);
  const [isShared, setIsShared] = useState(false);
  const [addCompanyOpen, setAddCompanyOpen] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [companies, setCompanies] = useState<Company[]>([]);

  const [summary, setSummary] = useState<CalculatedTotals>({
    customerId: 0,
    totalIncome: 0,
    totalAssets: 0,
    totalOutgoingsFixed: 0,
    totalOutgoingsFlexible: 0,
    totalOwingOnPriorityDebt: 0,
    totalRepayOnPriorityDebt: 0,
    totalOwingOnNonPriorityDebt: 0,
    totalRepayOnNonPriorityDebt: 0,
    pendingPaymentOffer: 0,
    balanceAmount: 0,
    totalRepaymentDebts: 0,
    totalOwingOnDebts: 0,
  });

  const [freepostCreditorNamesData, setFreepostCreditorNamesData] = useState(
    []
  );
  const [declarationData, setDeclarationData] = useState<
    CustomerDeclarationEntry[]
  >([]);
  const [ieMstrLenderData, setIeMstrLenderData] = useState<
    LenderParentRecord[]
  >([
    {
      id: 0,
      parentCompany: "",
      description: "",
      showCompanyList: false,
      referralCompanies: false,
      customerPaymentOffer: false,
      addToDebtList: false,
      referenceNumberFormat: "",
      childCompany: [],
      raw: { id: 0, lender: "" },
      childrenRaw: [],
    },
  ]);
  const [digitalLenders, setDigitalLenders] = useState<DigitalLender[]>([
    {
      schemaName: "",
      creditor: "",
      subDomain: "",
      lender: "",
      referenceNumberFormat: "",
      customerPaymentOffer: "",
      showCompanyList: "",
      addToDebtList: "",
      parentCompany: "",
      creditorAddress: "",
      isCreditorExistsWithIeHub: "",
    },
  ]);

  // Legacy parity: fire Meta Pixel + CAPI event on success screen
  useEffect(() => {
    void trackMetaEvent("Purchase", "Purchase");
  }, []);

  useEffect(() => {
    const fetchCompanies = async (): Promise<Company[]> => {
      const resolvedCustomerId: string | number | null = await getCustomerId();

      if (!resolvedCustomerId) {
        return [];
      }

      try {
        const coerceNumericId = (value: unknown): number | null => {
          if (typeof value === "number" && Number.isFinite(value)) return value;
          if (typeof value === "string") {
            const trimmed = value.trim();
            if (!trimmed) return null;
            const parsed = Number(trimmed);
            return Number.isFinite(parsed) ? parsed : null;
          }
          return null;
        };

        const isSharedRow = (row: any): boolean => {
          const submit =
            typeof row?.submitDate === "string" ? row.submitDate.trim() : "";
          const lastShared =
            typeof row?.lastSharedDate === "string"
              ? row.lastSharedDate.trim()
              : "";
          return Boolean(submit || lastShared);
        };

        /**
         * IMPORTANT (legacy parity):
         * - Keep ALL rows that were already shared (submitDate/lastSharedDate present),
         *   because history must remain visible (and reference edits after sharing create a new row).
         * - Only dedupe *pending/unshared* duplicates (no submitDate/lastSharedDate), since these
         *   are the "company shows twice" bug scenario.
         */
        const dedupeSharingRows = (rows: any[]): any[] => {
          const bestPendingByKey = new Map<string, any>();

          const keyForRow = (row: any): string => {
            const debtId = coerceNumericId(row?.debtId);
            const id = coerceNumericId(row?.id);
            if (typeof debtId === "number") return `debt:${debtId}`;
            if (typeof id === "number") return `id:${id}`;
            return `fallback:${String(row?.lenderName ?? row?.companyName ?? "")}|${String(
              row?.accountNumber ?? row?.referenceNumber ?? ""
            )}`;
          };

          // Pass 1: choose the "best" pending row per key (latest lastUpdateDate)
          for (const row of rows) {
            if (isSharedRow(row)) continue;
            const key = keyForRow(row);
            const prev = bestPendingByKey.get(key);
            const prevTs = prev?.lastUpdateDate
              ? Date.parse(prev.lastUpdateDate)
              : NaN;
            const nextTs = row?.lastUpdateDate
              ? Date.parse(row.lastUpdateDate)
              : NaN;
            if (
              !prev ||
              (Number.isFinite(nextTs) &&
                (!Number.isFinite(prevTs) || nextTs >= prevTs))
            ) {
              bestPendingByKey.set(key, row);
            }
          }

          // Pass 2: rebuild list preserving original order:
          // - include all shared rows
          // - include only the chosen pending row per key
          const emittedPendingKeys = new Set<string>();
          const out: any[] = [];
          for (const row of rows) {
            if (isSharedRow(row)) {
              out.push(row);
              continue;
            }
            const key = keyForRow(row);
            if (emittedPendingKeys.has(key)) continue;
            if (bestPendingByKey.get(key) === row) {
              out.push(row);
              emittedPendingKeys.add(key);
            }
          }
          return out;
        };

        const digitalDataRaw: any =
          await SharePageService.getCustomerSharing(resolvedCustomerId);
        const digitalData: any[] = Array.isArray(digitalDataRaw)
          ? dedupeSharingRows(digitalDataRaw)
          : [];

        const customerData = JSON.parse(
          localStorage.getItem("customerData") as string
        );

        const freePostDataRaw: any = await SharePageService.getFreePostData(
          customerData.customerId
        );
        const freePostData: any[] = Array.isArray(freePostDataRaw)
          ? dedupeSharingRows(freePostDataRaw)
          : [];

        const data = [
          ...(await Promise.all(
            digitalData.map(async (company: any) => {
              if (!company.lenderName) {
                return {};
              }

              // const correspondingDebt = debts.find((entry: any) => {
              //   return entry.id == company.debtId;
              // });

              let type = "all";

              const obj: Company = {
                id: company.id,
                name: company.lenderName,
                reference: company.accountNumber,
                type: type as Company["type"],
                address: company.address || "",
                documents: company.documents || [],
                selectedDocuments: [],
                lastShared: company.lastShared || "",
                raw: company,
              };

              return obj;
            })
          )),

          ...freePostData.map((company: any) => {
            let type = "all";

            const shareDate = company.lastSharedDate || company.submitDate;

            let obj: Company = {
              id: company.id,
              name: company.companyName,
              reference: company.referenceNumber,
              type: type as Company["type"],
              address: company.address || "",
              documents: company.documents || [],
              selectedDocuments: [],
              lastShared: company.lastShared || "",
              raw: company,
              isFp: true,
            };

            return obj;
          }),
        ];

        return data;
      } catch (error) {
        console.error("Failed to fetch companies", error);
        return [];
      }
    };

    fetchCompanies().then((companiesArray) => {
      setCompanies(companiesArray);
    });
  }, []);

  // GET Digital Creditor List Names
  useEffect(() => {
    async function getCreditorNames(): Promise<ServiceResponse<any[]>> {
      try {
        const response = await httpGet<ApiResponse>(
          customerEndpoints.listDigitalLenders
        );

        setDigitalLenders(response.data);
      } catch (error) {
        femaCapture("fetching_creditor_list_data_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
        throw error;
      }
    }

    getCreditorNames();
  }, []);

  // GET lender data
  useEffect(() => {
    async function getCustomerShareData() {
      const { data } = await LenderService.fetchLenders({
        skipErrorCapture: true,
      });

      setIeMstrLenderData(data);

      return;
    }

    getCustomerShareData();
  }, []);

  // GET balance amount (Calculated Totals) - required for showing Payment Offer section in Add Company flow
  useEffect(() => {
    const customerDataStr = localStorage.getItem("customerData");
    if (!customerDataStr) return;

    const customerData = JSON.parse(customerDataStr) as {
      customerId?: unknown;
    };
    const customerIdRaw = customerData?.customerId;
    const customerId =
      typeof customerIdRaw === "number"
        ? customerIdRaw
        : typeof customerIdRaw === "string" && customerIdRaw.trim().length > 0
          ? Number(customerIdRaw)
          : null;

    if (typeof customerId !== "number" || !Number.isFinite(customerId)) return;

    (async () => {
      try {
        const response = await httpGet<any>(
          customerEndpoints.getCalculatedTotals(customerId)
        );

        const raw = response?.data;
        const arr = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.data)
            ? raw.data
            : [];

        const nextBalanceRaw = arr?.[0]?.balanceAmount;
        const nextBalance =
          typeof nextBalanceRaw === "number"
            ? nextBalanceRaw
            : typeof nextBalanceRaw === "string" &&
                nextBalanceRaw.trim().length > 0
              ? Number(nextBalanceRaw)
              : 0;

        setBalanceAmount(Number.isFinite(nextBalance) ? nextBalance : 0);
      } catch (error) {
        femaCapture("fetching_calculated_totals_data_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
      }
    })();
  }, []);

  // GET declaration data
  useEffect(() => {
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string
    );

    async function getDeclarationData() {
      try {
        const response = await httpGet<ApiResponse>(
          declarationEndpoints.getDeclaration(customerData.customerId)
        );

        const data: any = response?.data;

        setDeclarationData(data);
      } catch (error) {
        femaCapture("fetching_declaration_data_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
        throw error;
      }
    }

    getDeclarationData();
  }, []);

  useEffect(() => {
    async function getFreepostCreditorNames(): Promise<ServiceResponse<any[]>> {
      try {
        const response = await httpGet<ApiResponse>(
          customerEndpoints.listFreepostLenders
        );
        return response.data;
      } catch (error) {
        femaCapture("fetching_freepost_creditor_list_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
        throw error;
      }
    }

    getFreepostCreditorNames().then((data: any) => {
      setFreepostCreditorNamesData(data);
    });
  }, []);

  useEffect(() => {
    setSharedCompanies(
      localStorage
        .getItem("companyList")
        ?.split(";")
        .filter(function (el) {
          return el.length != 0;
        }) || []
    );
  }, []);

  const hasFetchedSummary = useRef(false);

  // GET summary
  useEffect(() => {
    if (hasFetchedSummary.current) return;
    hasFetchedSummary.current = true;

    async function getSummary(): Promise<ServiceResponse<any[]>> {
      try {
        const resolvedCustomerId: string | number | null =
          await getCustomerId();

        const response = await httpGet<ApiResponse>(
          customerEndpoints.getCalculatedTotals(Number(resolvedCustomerId))
        );
        return response.data;
      } catch (error) {
        femaCapture("fetching_calculated_totals_data_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
        throw error;
      }
    }

    getSummary().then((res: any) => {
      setSummary(res[0]);
    });
  }, []);

  useEffect(() => {
    const isShared = localStorage.getItem("alreadyShared") == "Y";
    setIsShared(isShared);
  }, []);

  const handleShareWithCompany = () => {
    setAddCompanyOpen(true);
  };

  const handleSearchCompanies = () => {
    navigate("/freepost");
  };

  const handleGoToDashboard = () => {
    navigate("/dashboard");
  };

  const handleStartBenefitsCheck = () => {
    navigate("/benefits");
  };

  useEffect(() => {
    setBenefitsAmount(localStorage.getItem("BENEFITS_AMOUNT") || "0");
  }, []);

  const renderAddCompanyDialog = () => {
    return (
      <Dialog
        open={addCompanyOpen}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            ...(isMobile && {
              margin: 0,
              width: "100%",
              maxWidth: "100%",
              maxHeight: "90vh",
              borderRadius: "16px 16px 0 0",
              position: "fixed",
              bottom: 0,
            }),
            ...(!isMobile && {
              maxHeight: "90vh",
              padding: "0px 0px 20px 0px",
            }),
          },
        }}
        TransitionComponent={Transition as any}
        BackdropComponent={Backdrop}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
          },
        }}
      >
        <AddCompanyDialog
          onClose={() => {
            setAddCompanyOpen(false);
          }}
          balanceAmount={summary.balanceAmount}
          ieMstrLenderData={ieMstrLenderData}
          digitalLenders={digitalLenders}
          freepostCreditorNamesData={freepostCreditorNamesData}
          declarationData={declarationData}
          source="success"
          companies={companies}
        />
      </Dialog>
    );
  };

  return (
    <>
      {renderAddCompanyDialog()}

      {/* Mobile View */}
      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          flexDirection: "column",
          minHeight: "100vh",
          bgcolor: "#fafafa",
        }}
      >
        <Header />

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 2,
            py: 3,
            display: "flex",
            flexDirection: "column",
            gap: 3,
            pt: 11,
            pb: 18,
          }}
        >
          {/* Success Icon and Message */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              alignItems: "center",
              maxWidth: "500px",
              mx: "auto",
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                bgcolor:
                  sharedCompanies.length > 0 && !isShared
                    ? "#dcfce7"
                    : "#ff5e0036",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {sharedCompanies.length > 0 && !isShared ? (
                <CheckCircleIcon sx={{ fontSize: 32, color: "#16a34a" }} />
              ) : (
                <InfoIcon sx={{ fontSize: 32, color: "#FF5C00" }} />
              )}
            </Box>

            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  fontSize: "22px",
                  color: "#003b3e",
                  mb: 1,
                }}
              >
                {isShared
                  ? "You’ve Already Shared Your Budget Today"
                  : "Success!"}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "14px",
                  color: "#00243e",
                  opacity: 0.8,
                }}
              >
                {isShared
                  ? "It looks like you’ve already shared this budget form, and no changes have been made. To avoid confusion, the same budget can only be shared once per calendar day."
                  : sharedCompanies.length > 0
                    ? `Your budget has been successfully shared with ${sharedCompanies.length} ${sharedCompanies.length > 1 ? "companies" : "company"}`
                    : `Your budget has been saved in your account.`}
              </Typography>
            </Box>
          </Box>

          {sharedCompanies.length > 0 && !isShared && (
            <Paper
              sx={{
                p: 2,
                borderRadius: "12px",
                border: "1px solid #e4e7eb",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "#00243e",
                  mb: 2,
                }}
              >
                Shared with companies
              </Typography>

              {sharedCompanies.map((company, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: index < sharedCompanies.length - 1 ? 2 : 0,
                  }}
                >
                  <DotIcon sx={{ fontSize: 8, color: "#00243e" }} />
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#00243e",
                    }}
                  >
                    {company}
                  </Typography>
                </Box>
              ))}
            </Paper>
          )}

          {/* Benefits Check Promotional Card */}
          <Paper
            sx={{
              p: 3,
              borderRadius: "12px",
              border: "1px solid #155f54",
              background:
                "linear-gradient(284.97deg, #018374 1.26%, #004D44 99.98%)",
              boxShadow: "inset 0px 0px 10.9px rgba(119, 172, 255, 0.2)",
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Typography
                sx={{
                  fontSize: "20px",
                  fontWeight: 600,
                  color: "white",
                  lineHeight: "28px",
                }}
              >
                Complete a Benefits Check
              </Typography>
              <Typography
                sx={{
                  fontSize: "16px",
                  color: "#e1e5e5",
                  lineHeight: "normal",
                }}
              >
                Based on the information you have provided, we estimate that you
                could be entitled to an extra{" "}
                <Box component="span" sx={{ fontWeight: 600 }}>
                  £{benefitsAmount}
                </Box>
                . Use our tool to find out exactly how much more in benefits you
                could claim.
              </Typography>
            </Box>

            <Button
              onClick={handleStartBenefitsCheck}
              sx={{
                bgcolor: "white",
                color: "#155f54",
                borderRadius: "12px",
                py: 1,
                px: 1.5,
                fontSize: "16px",
                fontWeight: 600,
                textTransform: "none",
                alignSelf: "flex-start",
                "&:hover": {
                  bgcolor: "#f2f5f6",
                },
              }}
            >
              Start benefits check
            </Button>
          </Paper>

          {/* Share with Another Company */}
          <Paper
            sx={{
              p: 1.5,
              borderRadius: "12px",
              border: "1px solid #155f54",
              bgcolor: "#f2f5f6",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: "20px",
                fontWeight: 600,
                color: "#00243e",
                mb: 0.5,
              }}
            >
              Share with another company
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                color: "#6b7a7b",
                mb: 3,
              }}
            >
              You can send your budget to any other companies that are on our
              sharing list.
            </Typography>

            <Button
              fullWidth
              variant="contained"
              startIcon={<ShareIcon sx={{ fontSize: 14 }} />}
              onClick={handleShareWithCompany}
              sx={{
                bgcolor: "#155f54",
                color: "white",
                borderRadius: "8px",
                py: 1.25,
                fontSize: "14px",
                fontWeight: 500,
                textTransform: "none",
                "&:hover": {
                  bgcolor: "#0d4a42",
                },
              }}
            >
              Share with Company
            </Button>
          </Paper>

          {/* Can't See Your Company */}
          <Paper
            sx={{
              p: 1.5,
              borderRadius: "12px",
              border: "1px solid #155f54",
              bgcolor: "#f8f8f8",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: "20px",
                fontWeight: 600,
                color: "#00243e",
                mb: 0.5,
              }}
            >
              Can't see your company on our share list?
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                color: "#6b7a7b",
                mb: 3,
              }}
            >
              There are some companies we're not able to share with directly,
              but we may still be able to help. We offer free posting to a
              number of other companies, just click below to search and see if
              yours is included.
            </Typography>

            <Button
              fullWidth
              variant="outlined"
              startIcon={<SearchIcon sx={{ fontSize: 14 }} />}
              onClick={handleSearchCompanies}
              sx={{
                bgcolor: "white",
                color: "#155f54",
                borderColor: "#155f54",
                borderRadius: "8px",
                py: 1.25,
                fontSize: "14px",
                fontWeight: 500,
                textTransform: "none",
                "&:hover": {
                  bgcolor: "#f2f5f6",
                  borderColor: "#155f54",
                },
              }}
            >
              Search Companies
            </Button>
          </Paper>

          {/* Finished Sharing */}
          <Paper
            sx={{
              p: 1.5,
              borderRadius: "12px",
              border: "1px solid #c7c7c7",
              bgcolor: "#f8f8f8",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: "20px",
                fontWeight: 600,
                color: "#00243e",
                mb: 0.5,
              }}
            >
              Finished sharing?
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                color: "#6b7a7b",
                mb: 3,
              }}
            >
              Visit your dashboard, to see next steps, recommendations and
              anything else we can help you with.
            </Typography>

            <Button
              fullWidth
              variant="outlined"
              startIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
              onClick={handleGoToDashboard}
              sx={{
                bgcolor: "white",
                color: "#155f54",
                borderColor: "#929292",
                borderWidth: "0.5px",
                borderRadius: "8px",
                py: 1.25,
                fontSize: "14px",
                fontWeight: 500,
                textTransform: "none",
                "&:hover": {
                  bgcolor: "#f2f5f6",
                  borderColor: "#929292",
                },
              }}
            >
              Go to Dashboard
            </Button>
          </Paper>
        </Box>

        <BottomTabBar />
      </Box>

      {/* Desktop View */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          minHeight: "100vh",
          bgcolor: "#fafafa",
        }}
      >
        <Header />

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            display: "flex",
            justifyContent: "center",
            py: 4,
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: "742px",
              px: 2,
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            {/* Success Icon and Message */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                alignItems: "center",
                maxWidth: "500px",
                mx: "auto",
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  bgcolor:
                    sharedCompanies.length && !isShared > 0
                      ? "#dcfce7"
                      : "#ff5e0036",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {sharedCompanies.length > 0 && !isShared ? (
                  <CheckCircleIcon sx={{ fontSize: 32, color: "#16a34a" }} />
                ) : (
                  <InfoIcon sx={{ fontSize: 32, color: "#FF5C00" }} />
                )}
              </Box>

              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    fontSize: "22px",
                    color: "#003b3e",
                    mb: 1,
                  }}
                >
                  {isShared
                    ? "You’ve Already Shared Your Budget Today"
                    : "Success!"}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "14px",
                    color: "#00243e",
                    opacity: 0.8,
                  }}
                >
                  {isShared
                    ? "It looks like you’ve already shared this budget form, and no changes have been made. To avoid confusion, the same budget can only be shared once per calendar day."
                    : sharedCompanies.length > 0
                      ? `Your budget has been successfully shared with ${sharedCompanies.length} ${sharedCompanies.length > 1 ? "companies" : "company"}`
                      : `Your budget has been saved in your account.`}
                </Typography>
              </Box>
            </Box>

            {sharedCompanies.length > 0 && !isShared && (
              <Paper
                sx={{
                  p: 2,
                  borderRadius: "12px",
                  border: "1px solid #e4e7eb",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "18px",
                    fontWeight: 600,
                    color: "#00243e",
                    mb: 2,
                  }}
                >
                  Shared with companies
                </Typography>

                {sharedCompanies.map((company, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: index < sharedCompanies.length - 1 ? 2 : 0,
                    }}
                  >
                    <DotIcon sx={{ fontSize: 8, color: "#00243e" }} />
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "#00243e",
                      }}
                    >
                      {company}
                    </Typography>
                  </Box>
                ))}
              </Paper>
            )}

            {/* Benefits Check Promotional Card */}
            <Paper
              sx={{
                p: 3,
                borderRadius: "12px",
                border: "1px solid #155f54",
                background:
                  "linear-gradient(284.97deg, #018374 1.26%, #004D44 99.98%)",
                boxShadow: "inset 0px 0px 10.9px rgba(119, 172, 255, 0.2)",
                display: "flex",
                flexDirection: "column",
                gap: 3,
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Typography
                  sx={{
                    fontSize: "20px",
                    fontWeight: 600,
                    color: "white",
                    lineHeight: "28px",
                  }}
                >
                  Complete a Benefits Check
                </Typography>
                <Typography
                  sx={{
                    fontSize: "16px",
                    color: "#e1e5e5",
                    lineHeight: "normal",
                  }}
                >
                  Based on the information you have provided, we estimate that
                  you could be entitled to an extra{" "}
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    £{benefitsAmount}
                  </Box>
                  . Use our tool to find out exactly how much more in benefits
                  you could claim.
                </Typography>
              </Box>

              <Button
                onClick={handleStartBenefitsCheck}
                sx={{
                  bgcolor: "white",
                  color: "#155f54",
                  borderRadius: "12px",
                  py: 1,
                  px: 1.5,
                  fontSize: "16px",
                  fontWeight: 600,
                  textTransform: "none",
                  alignSelf: "flex-start",
                  "&:hover": {
                    bgcolor: "#f2f5f6",
                  },
                }}
              >
                Start benefits check
              </Button>
            </Paper>

            {/* Share with Another Company */}
            <Paper
              sx={{
                p: 1.5,
                borderRadius: "12px",
                border: "1px solid #155f54",
                bgcolor: "#f2f5f6",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontSize: "20px",
                  fontWeight: 600,
                  color: "#00243e",
                  mb: 0.5,
                }}
              >
                Share with another company
              </Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "#6b7a7b",
                  mb: 3,
                }}
              >
                You can send your budget to any other companies that are on our
                sharing list.
              </Typography>

              <Button
                variant="contained"
                startIcon={<ShareIcon sx={{ fontSize: 14 }} />}
                onClick={handleShareWithCompany}
                sx={{
                  bgcolor: "#155f54",
                  color: "white",
                  borderRadius: "8px",
                  py: 1.25,
                  px: 2,
                  fontSize: "14px",
                  fontWeight: 500,
                  textTransform: "none",
                  "&:hover": {
                    bgcolor: "#0d4a42",
                  },
                }}
              >
                Share with Company
              </Button>
            </Paper>

            {/* Can't See Your Company */}
            <Paper
              sx={{
                p: 1.5,
                borderRadius: "12px",
                border: "1px solid #c7c7c7",
                bgcolor: "#f8f8f8",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontSize: "20px",
                  fontWeight: 600,
                  color: "#00243e",
                  mb: 0.5,
                }}
              >
                Can't see your company on our share list?
              </Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "#6b7a7b",
                  mb: 3,
                }}
              >
                There are some companies we're not able to share with directly,
                but we may still be able to help. We offer free posting to a
                number of other companies, just click below to search and see if
                yours is included.
              </Typography>

              <Button
                variant="outlined"
                startIcon={<SearchIcon sx={{ fontSize: 14 }} />}
                onClick={handleSearchCompanies}
                sx={{
                  bgcolor: "white",
                  color: "#155f54",
                  borderColor: "#155f54",
                  borderRadius: "8px",
                  py: 1.25,
                  px: 2,
                  fontSize: "14px",
                  fontWeight: 500,
                  textTransform: "none",
                  "&:hover": {
                    bgcolor: "#f2f5f6",
                    borderColor: "#155f54",
                  },
                }}
              >
                Search Companies
              </Button>
            </Paper>

            {/* Finished Sharing */}
            <Paper
              sx={{
                p: 1.5,
                borderRadius: "12px",
                border: "1px solid #c7c7c7",
                bgcolor: "#f8f8f8",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontSize: "20px",
                  fontWeight: 600,
                  color: "#00243e",
                  mb: 0.5,
                }}
              >
                Finished sharing?
              </Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "#6b7a7b",
                  mb: 3,
                }}
              >
                Visit your dashboard, to see next steps, recommendations and
                anything else we can help you with.
              </Typography>

              <Button
                variant="outlined"
                startIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                onClick={handleGoToDashboard}
                sx={{
                  bgcolor: "white",
                  color: "#155f54",
                  borderColor: "#929292",
                  borderRadius: "8px",
                  py: 1.25,
                  px: 2,
                  fontSize: "14px",
                  fontWeight: 500,
                  textTransform: "none",
                  "&:hover": {
                    bgcolor: "#f2f5f6",
                    borderColor: "#929292",
                  },
                }}
              >
                Go to Dashboard
              </Button>
            </Paper>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default ShareSuccessPage;
